import { useState, useEffect } from "react";
import { Form, Button, Card, Container, Modal, InputGroup } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import Sidebar from "./SideBar";
import { List } from "react-bootstrap-icons";
import {
  db,
  doc,
  getDoc,
  updateDoc,
  signOut,
  auth,
  addDoc,
  collection,
  getDocs,
  deleteDoc,
} from ".."; // Import Firestore methods

const AdminSettings = () => {
  const navigate = useNavigate();
  const [howItWorksUrl, setHowItWorksUrl] = useState("");
  const [appVersion, setAppVersion] = useState("");
  const [appCountry, setAppCountry] = useState("");
  const [taskLimit, setTaskLimit] = useState(0);
  const [noteLimit, setNoteLimit] = useState(0);
  const [paymentLimit, setPaymentLimit] = useState(0);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const location = useLocation(); // Get the current route
  const [showSidebar, setShowSidebar] = useState(true);
  const [showCountryModal, setShowCountryModal] = useState(false);
  const [countries, setCountries] = useState([]); // Store the countries list
  const [selectedCountries, setSelectedCountries] = useState([]); // Track selected countries for deletion

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const docRef = doc(db, "app_config", "latest_version");
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          setHowItWorksUrl(data.video_url || "");
          setAppVersion(data.version || "");
          setTaskLimit(data.task_limit);
          setNoteLimit(data.note_limit);
          setPaymentLimit(data.payment_limit);
        }
      } catch (error) {
        console.error("Error fetching settings:", error);
      }
    };

    fetchSettings();
  }, []);

  // Function to fetch the countries
  const fetchCountries = async () => {
    try {
      const countriesRef = collection(db, "countries");
      const countriesSnap = await getDocs(countriesRef);
      const countriesList = countriesSnap.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name,
      }));
      setCountries(countriesList); // Set the countries list
    } catch (error) {
      console.error("Error fetching countries:", error);
    }
  };

  // Toggle the country modal
  const handleShowCountryModal = () => {
    fetchCountries(); // Fetch countries before opening the modal
    setShowCountryModal(true);
  };

  const handleCloseCountryModal = () => {
    setShowCountryModal(false);
  };

  // Handle country selection for deletion
  const handleCountrySelection = (event) => {
    const { value, checked } = event.target;
    setSelectedCountries((prevSelected) =>
      checked ? [...prevSelected, value] : prevSelected.filter((item) => item !== value)
    );
  };

  // Handle deletion of countries with alert confirmation
  const handleDeleteCountries = async () => {
    if (selectedCountries.length === 0) {
      alert("Please select at least one country to delete.");
      return;
    }

    const isConfirmed = window.confirm(
      "Are you sure you want to delete the selected countries?"
    );

    if (isConfirmed) {
      try {
        for (let countryId of selectedCountries) {
          const countryRef = doc(db, "countries", countryId);
          await deleteDoc(countryRef); // Delete country from Firestore
        }
        setSelectedCountries([]); // Clear selected countries after deletion
        fetchCountries(); // Refresh countries list
        alert("Selected countries deleted successfully!");
      } catch (error) {
        console.error("Error deleting countries:", error);
        alert("An error occurred while deleting countries.");
      }
    }
  };

  const handleUpdate = async (field, value) => {
    try {
      const settingsRef = doc(db, "app_config", "latest_version");
      const passwordRef = doc(db, "admin", "security");

      if (field === "How it Works URL") {
        if (!value) {
          alert("Please enter video url.");
          return;
        }
        await updateDoc(settingsRef, { video_url: value });
      } else if (field === "App Version") {
        if (!value) {
          alert("Please enter app version");
          return;
        }
        await updateDoc(settingsRef, { version: value });
      } else if (field === "Free Package Limits") {
        if (
          taskLimit === null || taskLimit === undefined || taskLimit === "" ||
          noteLimit === null || noteLimit === undefined || noteLimit === "" ||
          paymentLimit === null || paymentLimit === undefined || paymentLimit === ""
        ) {
          alert("Please fill out all fields.");
          return;
        }
        await updateDoc(settingsRef, {
          task_limit: taskLimit,
          note_limit: noteLimit,
          payment_limit: paymentLimit,
        });
      } else if (field === "Password") {
        if (!oldPassword || !newPassword || !confirmNewPassword) {
          alert("Please fill out all fields.");
          return;
        }
        const passwordRegex = /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*])[a-zA-Z0-9!@#$%^&*]{8,}$/;

        // Check if the new password meets the required format
        if (!passwordRegex.test(newPassword)) {
          alert("Password must be 8+ characters with at least 1 uppercase, 1 lowercase, and 1 special character.");
          return;
        }
        if (newPassword !== confirmNewPassword) {
          alert("New password and confirm password do not match.");
          return;
        }
        try {
          const docRef = doc(db, "admin", "security"); // Get the stored password
          const docSnap = await getDoc(docRef);
    
          if (docSnap.exists()) {
            const securityData = docSnap.data();
            const storedPassword = securityData.password.trim(); // Stored password from Firestore
    
            if (storedPassword === oldPassword) {
              // Update the password in Firestore
              await updateDoc(passwordRef, { password: newPassword });
              alert("Password updated successfully!");
              await signOut(auth);
              navigate("/", { replace: true });
              setOldPassword("");
              setNewPassword("");
              setConfirmNewPassword("");
              return;
            } else {
              alert("Old password is incorrect.");
              return;
            }
          }
        } catch (error) {
          console.error("Error updating password:", error);
          alert("An error occurred while updating password.");
          return;
        }
      } else if (field === "App Country") {
        if (!appCountry) {
          alert("Please enter a country name.");
          return;
        }
        const countryRef = collection(db, "countries");
        await addDoc(countryRef, { name: appCountry, value: appCountry });
        alert("Country added successfully!");
        setAppCountry(""); // Clear the country field after successful addition
        return;
      }
      alert(`${field} updated successfully!`);
    } catch (error) {
      console.error("Error updating Firestore:", error);
      alert("An error occurred while updating.");
    }
  };

  const toggleSidebar = () => {
    if (location.pathname !== "/dashboard") {
      setShowSidebar(!showSidebar);
    }
  };

  return (
    <Container
      fluid
      className="admin-dashboard d-flex"
      style={{ paddingBottom: 100 }}
    >
      <Sidebar showSidebar={showSidebar ? "show" : ""} />
      <Container
        className={`p-2 flex-grow-1 main-content ${
          showSidebar ? "shifted" : ""
        }`}
      >
        <Button variant="dark" onClick={toggleSidebar} className="mb-3">
          <List size={20} />
        </Button>
        <h2 className="mb-4 text-center">Settings</h2>

        {/* Update How It Works URL */}
        <Card className="mb-4">
          <Card.Body>
            <Card.Title>Update "How it Works" URL</Card.Title>
            <Form.Group className="d-flex gap-2">
              <Form.Control
                type="text"
                value={howItWorksUrl}
                onChange={(e) => setHowItWorksUrl(e.target.value)}
              />
              <Button
                variant="primary"
                onClick={() => handleUpdate("How it Works URL", howItWorksUrl)}
              >
                Update
              </Button>
            </Form.Group>
          </Card.Body>
        </Card>

        {/* Update App Version */}
        <Card className="mb-4">
          <Card.Body>
            <Card.Title>Update App Version</Card.Title>
            <Form.Group className="d-flex gap-2">
              <Form.Control
                type="text"
                value={appVersion}
                onChange={(e) => setAppVersion(e.target.value)}
              />
              <Button
                variant="primary"
                onClick={() => handleUpdate("App Version", appVersion)}
              >
                Update
              </Button>
            </Form.Group>
          </Card.Body>
        </Card>

        {/* Add App Country */}
        <Card className="mb-4">
          <Card.Body>
            <Card.Title>Add Country</Card.Title>
            <Form.Group className="d-flex gap-2">
              <Form.Control
                type="text"
                value={appCountry}
                onChange={(e) => setAppCountry(e.target.value)}
              />
              <Button
                variant="primary"
                onClick={() => handleUpdate("App Country", appCountry)}
              >
                Add
              </Button>
              <Button
                variant="secondary"
                onClick={handleShowCountryModal} // Show countries modal
              >
                View Added Countries
              </Button>
            </Form.Group>
          </Card.Body>
        </Card>

        {/* Manage Free Package Limits */}
        <Card className="mb-4">
          <Card.Body>
            <Card.Title>Manage Free Package Limits</Card.Title>
            <Form.Group className="mb-2">
              <Form.Label>Limit on Tasks</Form.Label>
              <Form.Control
                type="number"
                value={taskLimit}
                onChange={(e) => setTaskLimit(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Limit on Notes</Form.Label>
              <Form.Control
                type="number"
                value={noteLimit}
                onChange={(e) => setNoteLimit(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Limit on Payment Records</Form.Label>
              <Form.Control
                type="number"
                value={paymentLimit}
                onChange={(e) => setPaymentLimit(e.target.value)}
              />
            </Form.Group>
            <Button
              variant="primary"
              onClick={() => handleUpdate("Free Package Limits")}
            >
              Update Limits
            </Button>
          </Card.Body>
        </Card>

        {/* Password Change */}
        <Card className="mb-4">
          <Card.Body>
            <Card.Title>Update Password</Card.Title>
            <Form.Group className="mb-3">
              <Form.Label>Old Password</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showOldPassword ? "text" : "password"}
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  placeholder="Enter old password"
                />
                <InputGroup.Text
                  style={{ cursor: "pointer" }}
                  onClick={() => setShowOldPassword(!showOldPassword)}
                >
                  {showOldPassword ? "Hide" : "Show"}
                </InputGroup.Text>
              </InputGroup>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>New Password</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                />
                <InputGroup.Text
                  style={{ cursor: "pointer" }}
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? "Hide" : "Show"}
                </InputGroup.Text>
              </InputGroup>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Confirm New Password</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showConfirmNewPassword ? "text" : "password"}
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  placeholder="Confirm new password"
                />
                <InputGroup.Text
                  style={{ cursor: "pointer" }}
                  onClick={() =>
                    setShowConfirmNewPassword(!showConfirmNewPassword)
                  }
                >
                  {showConfirmNewPassword ? "Hide" : "Show"}
                </InputGroup.Text>
              </InputGroup>
            </Form.Group>
            <div style={{ color: 'red', margin: '10px' }}>
    {'*NOTE: Password must be 8+ characters with at least 1 uppercase, 1 lowercase, and 1 special character.'}
  </div>
            <Button
              variant="danger"
              onClick={() => handleUpdate("Password")}
            >
              Update Password
            </Button>
          </Card.Body>
        </Card>

      </Container>

      {/* Countries Modal */}
      <Modal show={showCountryModal} onHide={handleCloseCountryModal}>
        <Modal.Header closeButton>
          <Modal.Title>Manage Countries</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            {countries.map((country) => (
              <Form.Check
                key={country.id}
                type="checkbox"
                label={country.name}
                value={country.id}
                onChange={handleCountrySelection}
              />
            ))}
            <Button
              variant="danger"
              className="mt-3"
              onClick={handleDeleteCountries}
            >
              Delete Selected Countries
            </Button>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default AdminSettings;









// import { useState, useEffect } from "react";
// import { Form, Button, Card, Container } from "react-bootstrap";
// import { useLocation, useNavigate } from "react-router-dom";
// import Sidebar from "./SideBar";
// import { List } from "react-bootstrap-icons";
// import { db, doc, getDoc, updateDoc,signOut,auth,addDoc,collection} from ".."; // Import Firestore methods

// const AdminSettings = () => {
//   const navigate = useNavigate();
//   const [howItWorksUrl, setHowItWorksUrl] = useState("");
//   const [appVersion, setAppVersion] = useState("");
//   const [appCountry, setAppCountry] = useState("");
//   const [taskLimit, setTaskLimit] = useState(0);
//   const [noteLimit, setNoteLimit] = useState(0);
//   const [paymentLimit, setPaymentLimit] = useState(0);
//   const [newPassword, setNewPassword] = useState("");
//   const location = useLocation(); // Get the current route

//   const [showSidebar, setShowSidebar] = useState(true);

//   useEffect(() => {
//     const fetchSettings = async () => {
//       try {
//         const docRef = doc(db, "app_config", "latest_version");
//         const docSnap = await getDoc(docRef);

//         if (docSnap.exists()) {
//           const data = docSnap.data();
//           setHowItWorksUrl(data.video_url || "");
//           setAppVersion(data.version || "");
//           setTaskLimit(data.task_limit);
//           setNoteLimit(data.note_limit);
//           setPaymentLimit(data.payment_limit);
//         } else {
//           console.error("No such document!");
//         }
//       } catch (error) {
//         console.error("Error fetching settings:", error);
//       }
//     };

//     fetchSettings();
//   }, []); // Empty dependency array ensures this runs only once when component mounts

//   const handleUpdate = async (field, value) => {
//     try {
//       const settingsRef = doc(db, "app_config", "latest_version");
//       const passwordRef = doc(db, "admin", "security"); // Reference to the 'security' document in 'admin' collection

//       if (field === "How it Works URL") {
//         await updateDoc(settingsRef, {
//           video_url: value,
//         });
//       } else if (field === "App Version") {
//         await updateDoc(settingsRef, {
//           version: value,
//         });
//       } else if (field === "Free Package Limits") {
//         await updateDoc(settingsRef, {
//           task_limit: taskLimit,
//           note_limit: noteLimit,
//           payment_limit: paymentLimit,
//         });
//       } else if (field === "Password") {
//         await updateDoc(passwordRef, {
//           password: newPassword
//         });
//         // Handle password update logic here (e.g., Firebase Authentication)
//         alert("Password updated successfully!");
//         await signOut(auth);
//         // Redirect to login page after successful logout
//         navigate("/", { replace: true }); // Redirect to login page and replace current history entry
//         return;
//       }
//       if (field === "App Country") {
//         try {
//           // Reference to the 'countries' collection in Firestore (you can name it whatever you need)
//           const countryRef = collection(db, "countries");

//           // Add a new document with 'name' and 'value' fields
//           const docRef = await addDoc(countryRef, {
//             name: appCountry,  // Country name (e.g., "App Country")
//             value: appCountry  // Country value (e.g., "USA")
//           });

//           // Log the ID of the newly added document
//           console.log("New country added with ID: ", docRef.id);

//           // Alert user that the country was successfully added
//           alert("Country added successfully!");

//         } catch (error) {
//           console.error("Error adding country: ", error);
//         }
//         return;
//       }
//       alert(`${field} updated successfully!`);
//     } catch (error) {
//       console.error("Error updating Firestore:", error);
//       alert("An error occurred while updating.");
//     }
//   };

//   const toggleSidebar = () => {
//     if (location.pathname !== "/dashboard") {
//       setShowSidebar(!showSidebar);
//     }
//   };

//   return (
//     <Container fluid className="admin-dashboard d-flex" style={{ paddingBottom: 100 }}>
//       {/* Sidebar */}
//       <Sidebar showSidebar={showSidebar ? "show" : ""} />

//       {/* Main Content */}
//       <Container className={`p-2 flex-grow-1 main-content ${showSidebar ? "shifted" : ""}`}>
//         <Button variant="dark" onClick={toggleSidebar} className="mb-3">
//           <List size={20} />
//         </Button>
//         <h2 className="mb-4 text-center">Settings</h2>

//         {/* Update How It Works URL */}
//         <Card className="mb-4">
//           <Card.Body>
//             <Card.Title>Update "How it Works" URL</Card.Title>
//             <Form.Group className="d-flex gap-2">
//               <Form.Control
//                 type="text"
//                 value={howItWorksUrl}
//                 onChange={(e) => setHowItWorksUrl(e.target.value)}
//               />
//               <Button variant="primary" onClick={() => handleUpdate("How it Works URL", howItWorksUrl)}>
//                 Update
//               </Button>
//             </Form.Group>
//           </Card.Body>
//         </Card>

//         {/* Update App Version */}
//         <Card className="mb-4">
//           <Card.Body>
//             <Card.Title>Update App Version</Card.Title>
//             <Form.Group className="d-flex gap-2">
//               <Form.Control
//                 type="text"
//                 value={appVersion}
//                 onChange={(e) => setAppVersion(e.target.value)}
//               />
//               <Button variant="primary" onClick={() => handleUpdate("App Version", appVersion)}>
//                 Update
//               </Button>
//             </Form.Group>
//           </Card.Body>
//         </Card>
//   {/* Update App Countries */}
//   <Card className="mb-4">
//           <Card.Body>
//             <Card.Title>Add Country</Card.Title>
//             <Form.Group className="d-flex gap-2">
//               <Form.Control
//                 type="text"
//                 value={appCountry}
//                 onChange={(e) => setAppCountry(e.target.value)}
//               />
//               <Button variant="primary" onClick={() => handleUpdate("App Country", appCountry)}>
//                 Add
//               </Button>
//             </Form.Group>
//           </Card.Body>
//         </Card>
//         {/* Manage Free Package Limits */}
//         <Card className="mb-4">
//           <Card.Body>
//             <Card.Title>Manage Free Package Limits</Card.Title>
//             <Form.Group className="mb-2">
//               <Form.Label>Limit on Tasks</Form.Label>
//               <Form.Control
//                 type="number"
//                 value={taskLimit}
//                 onChange={(e) => setTaskLimit(e.target.value)}
//               />
//             </Form.Group>
//             <Form.Group className="mb-2">
//               <Form.Label>Limit on Notes</Form.Label>
//               <Form.Control
//                 type="number"
//                 value={noteLimit}
//                 onChange={(e) => setNoteLimit(e.target.value)}
//               />
//             </Form.Group>
//             <Form.Group className="mb-3">
//               <Form.Label>Limit on Payment Records</Form.Label>
//               <Form.Control
//                 type="number"
//                 value={paymentLimit}
//                 onChange={(e) => setPaymentLimit(e.target.value)}
//               />
//             </Form.Group>
//             <Button
//               variant="primary"
//               className="w-100"
//               onClick={() => handleUpdate("Free Package Limits")}
//             >
//               Update Limits
//             </Button>
//           </Card.Body>
//         </Card>

//         {/* Update Password */}
//         <Card>
//           <Card.Body>
//             <Card.Title>Update Password</Card.Title>
//             <Form.Group className="d-flex gap-2">
//               <Form.Control
//                 type="password"
//                 placeholder="New Password"
//                 value={newPassword}
//                 onChange={(e) => setNewPassword(e.target.value)}
//               />
//               <Button variant="danger" onClick={() => handleUpdate("Password")}>
//                 Update
//               </Button>
//             </Form.Group>
//           </Card.Body>
//         </Card>
//       </Container>
//     </Container>
//   );
// };

// export default AdminSettings;
